import { NavLink, matchPath, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Sidebar, SidebarSection, sidebarNavItemClassName } from '@domcrm/ui';
import { canShowNavItem } from '../../shared/access/access';
import { type SuperadminOrgTenantWideUiGateContext } from '../../shared/access/tenant-staff-governance';
import { isStaffPlatformMode } from '../../shared/access/staff-platform-mode';
import { cn } from '../../shared/lib/cn';
import { useAuth } from '../../shared/auth/use-auth';
import { usePortalUnreadBadge } from '../../features/feedback-journal/model/use-portal-unread';
import { ROUTE_PATHS } from '../../app/router/route-paths';
import { StaffContactDialog } from '../../pages/contact/StaffContactDialog';
import {
  orderStaffNavItemsForSidebarSection,
  resolveStaffNavSidebarSection,
  STAFF_NAV_CATALOG,
  type StaffNavCatalogEntry,
  type StaffNavSectionId
} from '../navigation/staff-route-catalog';
import { filterVisibleStaffNavItems, staffNavSectionTitle } from '../navigation/staff-nav-visibility';
import { staffNavIconForPath } from '../navigation/staff-nav-icons';
import { StaffDocsSidebarNav } from './staff-docs-sidebar-nav';
import styles from './staff-sidebar.module.css';

const SECTION_ORDER: StaffNavSectionId[] = [
  'shell',
  'organization',
  'users',
  'clients',
  'specialists',
  'schedule',
  'fitness',
  'service_catalog',
  'resources',
  'payments',
  'payment_governance',
  'sales',
  'assistant',
  'tasks',
  'automation',
  'journal',
  'yclients',
  'platform',
  'audit',
  'application',
  'account'
];

const SIDEBAR_SECTION_EXPANDED_KEY = 'domcrm-staff-sidebar-section-expanded';
/** По умолчанию раскрыты ключевые tenant-разделы; технические/редкие блоки раскрываются по активному маршруту или действию пользователя. */
const DEFAULT_EXPANDED_SECTIONS: ReadonlySet<StaffNavSectionId> = new Set([
  'shell',
  'organization',
  'users',
  'schedule'
]);
const SECTION_IDS = new Set<StaffNavSectionId>(SECTION_ORDER);

function readSectionExpanded(): Partial<Record<StaffNavSectionId, boolean>> {
  try {
    if (typeof localStorage === 'undefined') return {};
    const raw = localStorage.getItem(SIDEBAR_SECTION_EXPANDED_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Partial<Record<StaffNavSectionId, boolean>> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (!SECTION_IDS.has(key as StaffNavSectionId) || typeof value !== 'boolean') continue;
      out[key as StaffNavSectionId] = value;
    }
    return out;
  } catch {
    return {};
  }
}

function persistSectionExpanded(value: Partial<Record<StaffNavSectionId, boolean>>): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(SIDEBAR_SECTION_EXPANDED_KEY, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

function groupBySection(
  items: StaffNavCatalogEntry[],
  platformMode: boolean
): Map<StaffNavSectionId, StaffNavCatalogEntry[]> {
  const m = new Map<StaffNavSectionId, StaffNavCatalogEntry[]>();
  for (const e of items) {
    const section = resolveStaffNavSidebarSection(e, platformMode);
    const arr = m.get(section) ?? [];
    arr.push(e);
    m.set(section, arr);
  }
  return m;
}

function sectionHasActiveChild(items: StaffNavCatalogEntry[], pathname: string): boolean {
  return items.some((item) => {
    const pattern = { path: item.frontendPath, end: item.frontendPath === '/' };
    return matchPath(pattern, pathname) != null;
  });
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusableWithin(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.getAttribute('aria-hidden') !== 'true'
  );
}

export type StaffSidebarProps = {
  mobileOpen?: boolean;
  /** Мобильный контур: закрытый drawer убирается из accessibility tree и получает `inert`. */
  mobileDrawer?: boolean;
  onMobileClose?: () => void;
  /** Узкая колонка только с иконками (desktop). */
  collapsed?: boolean;
};

export function StaffSidebar({
  mobileOpen = false,
  mobileDrawer = false,
  onMobileClose,
  collapsed = false
}: StaffSidebarProps) {
  const { me, impersonateOrgId, bootstrapState } = useAuth();
  const portalUnread = usePortalUnreadBadge();
  const cap = me?.capabilities;
  const location = useLocation();
  const platformMode = isStaffPlatformMode(me, impersonateOrgId);
  const orgBootstrap =
    bootstrapState.kind === 'authenticated_with_org_context' || bootstrapState.kind === 'impersonating_org';
  const saOrgWideGateCtx = useMemo<SuperadminOrgTenantWideUiGateContext>(
    () => ({ me, orgBootstrap }),
    [me, orgBootstrap]
  );
  const visibleItems = filterVisibleStaffNavItems(STAFF_NAV_CATALOG, cap, platformMode, saOrgWideGateCtx, me, impersonateOrgId);
  const grouped = groupBySection(visibleItems, platformMode);
  const ControlsIcon = staffNavIconForPath(ROUTE_PATHS.controls);
  const docsCatalogEntry = useMemo(() => STAFF_NAV_CATALOG.find((e) => e.backendRouteKey === '/docs'), []);
  const contactCatalogEntry = useMemo(() => STAFF_NAV_CATALOG.find((e) => e.backendRouteKey === '/contact'), []);
  const canShowDocsFooter = docsCatalogEntry != null && canShowNavItem(cap, docsCatalogEntry);
  /** В режиме платформы (СА без орг.) футер «Оставить отзыв» скрыт; страница /feedback — журнал без формы. */
  const canShowContactFooter =
    contactCatalogEntry != null && canShowNavItem(cap, contactCatalogEntry) && !platformMode;
  const onDocsRoute = location.pathname === ROUTE_PATHS.docs;
  const DocsIcon = staffNavIconForPath('/docs');
  const ContactIcon = staffNavIconForPath('/contact');
  /** В мобильном drawer при открытом меню всегда полные подписи, даже если desktop был в узком режиме. */
  const narrowNav = collapsed && !mobileOpen;

  /** Отсутствие ключа = дефолт проекта: «Общее» и «Организация» открыты, остальные закрыты. */
  const [sectionExpanded, setSectionExpanded] = useState<Partial<Record<StaffNavSectionId, boolean>>>(
    readSectionExpanded
  );
  const [contactOpen, setContactOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const asideRef = useRef<HTMLElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const trimmedQuery = searchQuery.trim().toLowerCase();

  const isSectionOpen = useCallback(
    (sectionId: StaffNavSectionId) => sectionExpanded[sectionId] ?? DEFAULT_EXPANDED_SECTIONS.has(sectionId),
    [sectionExpanded]
  );

  const setSectionOpen = useCallback((sectionId: StaffNavSectionId, open: boolean) => {
    setSectionExpanded((prev) => ({ ...prev, [sectionId]: open }));
  }, []);

  useEffect(() => {
    persistSectionExpanded(sectionExpanded);
  }, [sectionExpanded]);

  useEffect(() => {
    const visible = filterVisibleStaffNavItems(STAFF_NAV_CATALOG, cap, platformMode, saOrgWideGateCtx, me, impersonateOrgId);
    const map = groupBySection(visible, platformMode);
    setSectionExpanded((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const sectionId of SECTION_ORDER) {
        const items = map.get(sectionId);
        if (!items?.length) continue;
        if (items.length <= 1 && sectionId !== 'audit') continue;
        if (sectionHasActiveChild(items, location.pathname) && next[sectionId] === false) {
          next[sectionId] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [location.pathname, cap, platformMode, saOrgWideGateCtx, me, impersonateOrgId]);

  useEffect(() => {
    if (!mobileOpen) return;
    const aside = asideRef.current;
    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const initialFocus = aside ?? focusableWithin(aside)[0];
    initialFocus?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onMobileClose?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = focusableWithin(asideRef.current);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const focusIsOutside = !(active instanceof Node) || !asideRef.current?.contains(active);
      const activeIndex = active instanceof HTMLElement ? focusable.indexOf(active) : -1;
      if (event.shiftKey) {
        if (focusIsOutside || activeIndex <= 0) {
          event.preventDefault();
          last.focus();
        }
      } else if (focusIsOutside || activeIndex === -1 || activeIndex === focusable.length - 1) {
        event.preventDefault();
        first.focus();
      }
    };

    const onFocusIn = (event: FocusEvent) => {
      const aside = asideRef.current;
      if (!aside || !(event.target instanceof Node) || aside.contains(event.target)) return;
      focusableWithin(aside)[0]?.focus();
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('focusin', onFocusIn);
      const restore = restoreFocusRef.current;
      restoreFocusRef.current = null;
      if (restore && document.contains(restore)) restore.focus();
    };
  }, [mobileOpen, onMobileClose]);

  const drawerHidden = mobileDrawer && !mobileOpen;

  return (
    <aside
      ref={asideRef}
      id="staff-sidebar-nav"
      className={cn(styles.sidebar, mobileOpen && styles.sidebarOpen, collapsed && styles.sidebarCollapsed)}
      role={mobileDrawer && mobileOpen ? 'dialog' : undefined}
      aria-modal={mobileDrawer && mobileOpen ? true : undefined}
      aria-label={mobileDrawer && mobileOpen ? 'Меню навигации' : 'Разделы'}
      inert={drawerHidden}
      tabIndex={mobileDrawer ? -1 : undefined}
      data-collapsed={narrowNav ? 'true' : undefined}
    >
      {onDocsRoute && canShowDocsFooter ? (
        <StaffDocsSidebarNav narrowNav={narrowNav} onMobileClose={onMobileClose} />
      ) : (
        <>
          {!narrowNav && (
            <div className={styles.sidebarSearch}>
              <Search className={styles.sidebarSearchIcon} aria-hidden />
              <input
                ref={searchInputRef}
                type="search"
                className={styles.sidebarSearchInput}
                placeholder="Найти раздел…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Поиск по разделам"
              />
              {searchQuery && (
                <button
                  type="button"
                  className={styles.sidebarSearchClear}
                  aria-label="Сбросить поиск"
                  onClick={() => { setSearchQuery(''); searchInputRef.current?.focus(); }}
                >
                  <X className="size-3" aria-hidden />
                </button>
              )}
            </div>
          )}
          <Sidebar className={styles.sidebarNav}>
            {SECTION_ORDER.map((sectionId) => {
              const raw = grouped.get(sectionId);
              if (!raw?.length) return null;
              const allItems = orderStaffNavItemsForSidebarSection(sectionId, raw);
              const items = trimmedQuery
                ? allItems.filter((item) => item.label.toLowerCase().includes(trimmedQuery))
                : allItems;
              if (trimmedQuery && items.length === 0) return null;
              const title = sidebarSectionTitle(sectionId, allItems.length);
              const isExpanded = trimmedQuery ? true : (title ? isSectionOpen(sectionId) : undefined);

              return (
                <SidebarSection
                  key={sectionId}
                  title={title}
                  collapsed={narrowNav}
                  expanded={isExpanded}
                  onExpandedChange={title && !trimmedQuery ? (open) => setSectionOpen(sectionId, open) : undefined}
                >
                  {items.map((item) => {
                    const Icon = staffNavIconForPath(item.backendRouteKey);
                    const label = item.backendRouteKey === '/settings' && !platformMode ? 'Настройки компании' : item.label;
                    return (
                      <NavLink
                        key={item.backendRouteKey}
                        to={item.frontendPath}
                        aria-label={narrowNav ? label : undefined}
                        title={narrowNav ? label : undefined}
                        className={({ isActive }) =>
                          cn(sidebarNavItemClassName(isActive), 'relative', narrowNav && 'justify-center px-1.5')
                        }
                        end={item.frontendPath === '/'}
                        onClick={() => { setSearchQuery(''); onMobileClose?.(); }}
                      >
                        <Icon className="size-4 shrink-0 text-[hsl(var(--ui-muted-foreground))]" aria-hidden />
                        {narrowNav ? null : (
                          <span className="min-w-0 flex-1 truncate">{label}</span>
                        )}
                        {item.backendRouteKey === '/feedback' && portalUnread > 0 ? (
                          <span
                            className={cn(
                              'flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-[11px] font-bold text-white',
                              narrowNav && 'absolute -top-1 -right-1'
                            )}
                            aria-label={`Непрочитанных ответов: ${portalUnread}`}
                          >
                            {portalUnread > 9 ? '9+' : portalUnread}
                          </span>
                        ) : null}
                      </NavLink>
                    );
                  })}
                </SidebarSection>
              );
            })}
          </Sidebar>
        </>
      )}
      <div className={styles.sidebarFooter}>
        {platformMode ? (
          <NavLink
            to={ROUTE_PATHS.controls}
            aria-label={narrowNav ? 'Контролы (витрина UI)' : undefined}
            title={narrowNav ? 'Контролы (витрина UI)' : 'Витрина UI для платформенного режима'}
            className={({ isActive }) =>
              cn(sidebarNavItemClassName(isActive), 'relative', narrowNav && 'justify-center px-1.5')
            }
            onClick={() => onMobileClose?.()}
          >
            <ControlsIcon className="size-4 shrink-0 text-[hsl(var(--ui-muted-foreground))]" aria-hidden />
            {narrowNav ? null : (
              <span className="min-w-0 flex-1 truncate">Контролы (витрина UI)</span>
            )}
          </NavLink>
        ) : null}
        {canShowDocsFooter ? (
          <NavLink
            to={ROUTE_PATHS.docs}
            aria-label={narrowNav ? 'Документация' : undefined}
            title={narrowNav ? 'Документация' : undefined}
            className={({ isActive }) =>
              cn(sidebarNavItemClassName(isActive), 'relative', narrowNav && 'justify-center px-1.5')
            }
            onClick={() => onMobileClose?.()}
          >
            <DocsIcon className="size-4 shrink-0 text-[hsl(var(--ui-muted-foreground))]" aria-hidden />
            {narrowNav ? null : <span className="min-w-0 flex-1 truncate">Документация</span>}
          </NavLink>
        ) : null}
        {canShowContactFooter ? (
          <button
            type="button"
            aria-label={narrowNav ? 'Оставить отзыв' : undefined}
            title={narrowNav ? 'Оставить отзыв' : undefined}
            className={cn(sidebarNavItemClassName(location.pathname === ROUTE_PATHS.contact), narrowNav && 'justify-center px-1.5')}
            onClick={() => {
              setContactOpen(true);
              onMobileClose?.();
            }}
          >
            <ContactIcon className="size-4 shrink-0 text-[hsl(var(--ui-muted-foreground))]" aria-hidden />
            {narrowNav ? null : <span className="min-w-0 flex-1 truncate">Оставить отзыв</span>}
          </button>
        ) : null}
      </div>
      <StaffContactDialog open={contactOpen} onOpenChange={setContactOpen} />
    </aside>
  );
}

/** Заголовок группы: для ключевых tenant-разделов — даже при одном пункте, чтобы IA не смешивалась. */
function sidebarSectionTitle(sectionId: StaffNavSectionId, itemCount: number): string | undefined {
  if (itemCount === 0) return undefined;
  if (itemCount > 1) return staffNavSectionTitle(sectionId);
  if (
    sectionId === 'audit' ||
    sectionId === 'organization' ||
    sectionId === 'resources' ||
    sectionId === 'users' ||
    sectionId === 'schedule'
  ) {
    return staffNavSectionTitle(sectionId);
  }
  return undefined;
}
